import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Button } from '@edusense/shared-ui-kit';
import { Tooltip } from '@heroui/react';
import BrandLogo from '../common/BrandLogo';
import { HeaderGradientOverlay, ResizeHandle, ResizeHandleIndicator } from './ChatModal.styles';
import ChatContainer from '../chat/ChatContainer';
import ChatInput from '../chat/ChatInput';
import ChatHistoryView from '../history/ChatHistoryView';
import GeneralSettingsView from '../settings/GeneralSettingsView';
// Lucide React 직접 import
import { Plus, Sparkles, History, Settings, User, Trash2 } from 'lucide-react';
import { ChevronDownIcon, RectangleStackIcon, RectangleGroupIcon } from '@heroicons/react/24/outline';
const AIChatMainView = ({ isMobile, isMinimized, isMaximized, isCompact, position, size, currentView, currentChatId, isDragging, isResizing, isCreatingChat, isGenerating, messages, handleMouseDown, handleTouchStart, setCurrentView, toggleMaximize, handleCopyMessage, handleSendMessage, handleCreateNewChat, handleClearMessages, handleResizeStart, stopGeneration, onClose, isSidebarMode, onSwitchToUserChat })=>{
    const { t } = useTranslation('chat');
    const modalRef = useRef(null);
    const headerRef = useRef(null);
    // Fullscreen overlay 모드: sidebar 모드가 아니면 항상 전체 화면으로 렌더링
    const isOverlay = !isSidebarMode;
    // 모달 스타일 계산
    const modalStyle = {
        position: 'fixed',
        left: isOverlay ? 0 : position.x,
        top: isOverlay ? 0 : position.y,
        width: isOverlay ? '100vw' : size.width,
        height: isOverlay ? '100vh' : size.height,
        zIndex: 9999,
        pointerEvents: 'auto',
        transform: 'none',
        transformOrigin: 'center'
    };
    const resizeHandles = [
        'n',
        's',
        'e',
        'w',
        'nw',
        'ne',
        'sw',
        'se'
    ];
    return /*#__PURE__*/ _jsxs(motion.div, {
        ref: modalRef,
        style: modalStyle,
        initial: {
            opacity: 0,
            scale: 0.9,
            y: 20
        },
        animate: {
            opacity: isDragging ? 0.95 : 1,
            scale: 1,
            y: 0,
            transition: {
                type: "spring",
                stiffness: 300,
                damping: 30
            }
        },
        exit: {
            opacity: 0,
            scale: 0.9,
            y: 20
        },
        className: `
        ${isMaximized || isMobile || isOverlay ? 'rounded-none' : 'rounded-3xl'}
        ${isDragging ? 'cursor-grabbing' : ''}
        bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl backdrop-saturate-150 shadow-2xl shadow-black/10 dark:shadow-black/20 ring-1 ring-black/5 dark:ring-white/10
        overflow-hidden
      `,
        children: [
            /*#__PURE__*/ _jsxs(motion.div, {
                ref: headerRef,
                className: `
          flex items-center justify-between 
          ${isMobile ? 'p-2.5' : 'p-2'} 
          border-b border-gray-200/50 dark:border-gray-800/50
          bg-gradient-to-r from-gray-50/30 to-gray-100/20 dark:from-gray-800/30 dark:to-gray-900/20
          select-none relative overflow-hidden
        `,
                /* floating 금지: overlay/side 모두 드래그 비활성화 */ onMouseDown: undefined,
                onTouchStart: undefined,
                style: {
                    cursor: 'default',
                    touchAction: 'auto',
                    WebkitTouchCallout: 'none',
                    WebkitUserSelect: 'none',
                    userSelect: 'none'
                },
                children: [
                    /*#__PURE__*/ _jsx(HeaderGradientOverlay, {}),
                    /*#__PURE__*/ _jsx("div", {
                        className: `flex items-center ${isMobile ? 'space-x-2' : isCompact ? 'space-x-1.5' : 'space-x-2'} relative z-10`,
                        children: /*#__PURE__*/ _jsx(Tooltip, {
                            content: "AI 튜터입니다. 궁금한 것을 물어보세요.",
                            placement: "bottom",
                            delay: 0,
                            closeDelay: 0,
                            className: "px-2 py-1 text-xs",
                            children: /*#__PURE__*/ _jsxs(motion.div, {
                                whileHover: {
                                    scale: 1.05
                                },
                                whileTap: {
                                    scale: 0.95
                                },
                                className: `
                ${isMobile ? 'p-2.5' : 'p-2'} 
                bg-gradient-to-br from-blue-500 to-purple-600 dark:from-blue-600 dark:to-purple-700
                rounded-xl shadow-md shadow-blue-500/20 dark:shadow-blue-500/15
                relative overflow-hidden cursor-pointer
              `,
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "absolute inset-0 bg-gradient-to-tr from-white/15 to-transparent"
                                    }),
                                    /*#__PURE__*/ _jsx(BrandLogo, {
                                        size: isMobile ? 'lg' : 'md',
                                        className: "text-white relative z-10"
                                    }),
                                    /*#__PURE__*/ _jsx(Sparkles, {
                                        className: "absolute -top-0.5 -right-0.5 w-2.5 h-2.5 text-yellow-300 animate-pulse"
                                    })
                                ]
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center space-x-1 relative z-10",
                        children: [
                            onSwitchToUserChat && /*#__PURE__*/ _jsx(Tooltip, {
                                content: "메시지로 전환",
                                placement: "bottom",
                                delay: 0,
                                closeDelay: 0,
                                className: "px-2 py-1 text-xs",
                                children: /*#__PURE__*/ _jsx(Button, {
                                    iconOnly: true,
                                    icon: /*#__PURE__*/ _jsx(User, {
                                        className: "w-3.5 h-3.5"
                                    }),
                                    variant: "ghost",
                                    size: "sm",
                                    onClick: onSwitchToUserChat,
                                    label: "메시지"
                                })
                            }),
                            /*#__PURE__*/ _jsx(Tooltip, {
                                content: "채팅 기록 보기",
                                placement: "bottom",
                                delay: 0,
                                closeDelay: 0,
                                className: "px-2 py-1 text-xs",
                                children: /*#__PURE__*/ _jsx(Button, {
                                    iconOnly: true,
                                    icon: /*#__PURE__*/ _jsx(History, {
                                        className: "w-3.5 h-3.5"
                                    }),
                                    variant: "ghost",
                                    size: "sm",
                                    onClick: ()=>setCurrentView('history'),
                                    label: "채팅 기록"
                                })
                            }),
                            /*#__PURE__*/ _jsx(Tooltip, {
                                content: "MCP 설정",
                                placement: "bottom",
                                delay: 0,
                                closeDelay: 0,
                                className: "px-2 py-1 text-xs",
                                children: /*#__PURE__*/ _jsx(Button, {
                                    iconOnly: true,
                                    icon: /*#__PURE__*/ _jsx(Settings, {
                                        className: "w-3.5 h-3.5"
                                    }),
                                    variant: "ghost",
                                    size: "sm",
                                    onClick: ()=>setCurrentView('settings'),
                                    label: "MCP 설정"
                                })
                            }),
                            !(isMaximized || isMobile) && !isOverlay && /*#__PURE__*/ _jsx(Tooltip, {
                                content: "최대화",
                                placement: "bottom",
                                delay: 0,
                                closeDelay: 0,
                                className: "px-2 py-1 text-xs",
                                children: /*#__PURE__*/ _jsx(Button, {
                                    iconOnly: true,
                                    icon: /*#__PURE__*/ _jsx(RectangleStackIcon, {
                                        className: "w-3.5 h-3.5"
                                    }),
                                    variant: "ghost",
                                    size: "sm",
                                    onClick: toggleMaximize,
                                    label: "최대화"
                                })
                            }),
                            isMaximized && !isMobile && !isOverlay && /*#__PURE__*/ _jsx(Tooltip, {
                                content: "원래 크기로",
                                placement: "bottom",
                                delay: 0,
                                closeDelay: 0,
                                className: "px-2 py-1 text-xs",
                                children: /*#__PURE__*/ _jsx(Button, {
                                    iconOnly: true,
                                    icon: /*#__PURE__*/ _jsx(RectangleGroupIcon, {
                                        className: "w-3.5 h-3.5"
                                    }),
                                    variant: "ghost",
                                    size: "sm",
                                    onClick: toggleMaximize,
                                    label: "원래 크기로"
                                })
                            }),
                            /*#__PURE__*/ _jsx(Tooltip, {
                                content: "닫기",
                                placement: "bottom",
                                delay: 0,
                                closeDelay: 0,
                                className: "px-2 py-1 text-xs",
                                children: /*#__PURE__*/ _jsx(Button, {
                                    iconOnly: true,
                                    icon: /*#__PURE__*/ _jsx(ChevronDownIcon, {
                                        className: "w-3.5 h-3.5"
                                    }),
                                    variant: "ghost",
                                    size: "sm",
                                    onClick: onClose,
                                    label: "닫기"
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col",
                style: {
                    height: `calc(100% - ${isMobile ? '60px' : '52px'})`
                },
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex-1 bg-gradient-to-b from-gray-50/50 to-white dark:from-gray-900/50 dark:to-gray-900 overflow-hidden",
                        children: currentView === 'history' ? /*#__PURE__*/ _jsx(ChatHistoryView, {
                            onBack: ()=>setCurrentView('chat')
                        }) : currentView === 'settings' ? /*#__PURE__*/ _jsx(GeneralSettingsView, {
                            onBack: ()=>setCurrentView('chat')
                        }) : /*#__PURE__*/ _jsx(ChatContainer, {
                            messages: messages,
                            isLoading: isGenerating,
                            onCopy: handleCopyMessage,
                            loadingMessage: t('ai.generating')
                        })
                    }),
                    currentView === 'chat' && /*#__PURE__*/ _jsx("div", {
                        className: `border-t border-gray-200/50 dark:border-gray-800/50 bg-gradient-to-b from-white to-gray-50/50 dark:from-gray-900 dark:to-gray-800/50 ${isMobile ? 'p-2' : 'p-3'}`,
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: `${isMobile ? 'space-y-2' : 'space-y-3'}`,
                            children: [
                                /*#__PURE__*/ _jsx(ChatInput, {
                                    onSendMessage: handleSendMessage,
                                    isLoading: isGenerating,
                                    onStopGeneration: stopGeneration,
                                    placeholder: t('input.placeholder'),
                                    isCompact: isCompact
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "flex items-center justify-between",
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-1",
                                        children: [
                                            currentChatId && /*#__PURE__*/ _jsx(Button, {
                                                variant: "primary",
                                                size: "xs",
                                                leftIcon: /*#__PURE__*/ _jsx(Plus, {
                                                    className: "w-3 h-3"
                                                }),
                                                onClick: (e)=>{
                                                    e.preventDefault();
                                                    handleCreateNewChat();
                                                },
                                                disabled: isCreatingChat,
                                                isLoading: isCreatingChat,
                                                children: t('newConversation')
                                            }),
                                            currentChatId && /*#__PURE__*/ _jsx(Button, {
                                                variant: "ghost",
                                                size: "xs",
                                                leftIcon: /*#__PURE__*/ _jsx(Trash2, {
                                                    className: "w-3 h-3"
                                                }),
                                                onClick: (e)=>{
                                                    e.preventDefault();
                                                    void handleClearMessages();
                                                },
                                                disabled: messages.length === 0 || isGenerating,
                                                children: t('history.clearHistory', {
                                                    defaultValue: '기록 삭제'
                                                })
                                            })
                                        ]
                                    })
                                })
                            ]
                        })
                    })
                ]
            }),
            false && resizeHandles.map((direction)=>/*#__PURE__*/ _jsx(ResizeHandle, {
                    direction: direction,
                    onMouseDown: (e)=>handleResizeStart(e, direction),
                    onTouchStart: (e)=>handleResizeStart(e.touches[0], direction),
                    children: /*#__PURE__*/ _jsx(ResizeHandleIndicator, {
                        direction: direction
                    })
                }, direction))
        ]
    });
};
export default AIChatMainView;
